// ─── vinDecoder.js ────────────────────────────────────────────────────────────
// NHTSA VIN decoder — free public API, no CORS restrictions, no API key needed.
// Docs: https://vpic.nhtsa.dot.gov/api/

const NHTSA_BASE = 'https://vpic.nhtsa.dot.gov/api/vehicles';

// Cache decoded VINs in sessionStorage so we don't re-fetch on re-render
const CACHE_KEY = 'wm-vin-cache-v1';

function readCache() {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
}
function writeCache(cache) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* ignore */ }
}

/**
 * Decode a VIN using the NHTSA vPIC API.
 * Returns a normalized object or null on failure.
 *
 * @param {string} vin — 17-character VIN
 * @returns {Promise<DecodedVin | null>}
 */
export async function decodeVIN(vin) {
  if (!vin || vin.length !== 17) return null;

  const upper = vin.toUpperCase();
  const cache = readCache();
  if (cache[upper]) return cache[upper];

  try {
    const res = await fetch(`${NHTSA_BASE}/decodevin/${upper}?format=json`);
    if (!res.ok) return null;
    const json = await res.json();
    const results = json?.Results ?? [];

    const get = (varName) => {
      const r = results.find(r => r.Variable === varName);
      return r?.Value && r.Value !== 'Not Applicable' ? r.Value : null;
    };

    const decoded = {
      vin: upper,
      year:       get('Model Year'),
      make:       get('Make'),
      model:      get('Model'),
      trim:       get('Trim'),
      bodyStyle:  get('Body Class'),
      engine:     get('Displacement (L)') ? `${get('Displacement (L)')}L` : null,
      fuelType:   get('Fuel Type - Primary'),
      driveType:  get('Drive Type'),
      doors:      get('Number of Doors'),
      errorCode:  get('Error Code'),
      errorText:  get('Error Text'),
      valid:      get('Error Code') === '0',
    };

    // Cache for session
    const updated = { ...cache, [upper]: decoded };
    writeCache(updated);

    return decoded;
  } catch {
    return null;
  }
}

/**
 * Validate VIN format (basic checksum + length check).
 * NHTSA API still accepts malformed VINs and returns error codes,
 * but we can save a round-trip for obvious non-VINs.
 */
export function isValidVINFormat(vin) {
  if (!vin || vin.length !== 17) return false;
  // VINs use chars A–Z (no I, O, Q) and 0–9
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
}

/**
 * Format a decoded VIN result as a vehicle label string.
 * e.g. "2023 Tesla Model 3"
 */
export function formatVehicleLabel(decoded) {
  if (!decoded) return '';
  return [decoded.year, decoded.make, decoded.model, decoded.trim]
    .filter(Boolean).join(' ');
}
